// Layer 3 (Transformation): pure projections over READING_SERIES + SessionState.
// No React, no engine mutations.

import type { Reading } from '@/versions/v1/schema/reading';
import type { Perturbation, SessionState } from '@/versions/v1/schema/scenario';
import type { ReadingSeries, SeriesTime, SeriesView } from '@/versions/v1/schema/reading-series';
import { HOURLY_FEED_DIVISOR, HOURLY_FEED_READING_IDS, READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import {
  READING_SERIES,
  SERIES_BY_ID,
  SERIES_BY_READING_ID,
} from '@/versions/v1/shared/bunker-hill-series';
import { addMinutes, diffMinutes, formatDayLabel, timeMilliseconds } from '@/versions/v1/shared/clock.derive';
import { perturbationsFor } from '@/versions/v1/shared/perturbations';

function cadenceStep(cadence: ReadingSeries['cadence']): number {
  if (cadence === 'hourly') return 60;
  if (cadence === 'shift') return 12 * 60;
  return 24 * 60;
}

type IndexedSeriesStamps = { stamps: SeriesTime[]; epochMs: number[] };
const STAMPS_BY_SERIES_ID = new Map<string, IndexedSeriesStamps>();

function seriesStamps(series: ReadingSeries): IndexedSeriesStamps {
  const cached = STAMPS_BY_SERIES_ID.get(series.id);
  if (cached) return cached;
  const stamps: SeriesTime[] = [];
  const epochMs: number[] = [];
  const step = cadenceStep(series.cadence);
  let t = series.start;
  for (let i = 0; i < series.points.length; i++) {
    stamps.push(t);
    epochMs.push(timeMilliseconds(t));
    t = addMinutes(t, step);
  }
  const indexed = { stamps, epochMs };
  STAMPS_BY_SERIES_ID.set(series.id, indexed);
  return indexed;
}

/** Last point whose stamp is at or before the cursor, found in log n time. */
function lastIndexAtOrBefore(epochMs: readonly number[], cursorMs: number): number {
  // The old scan included every point when the cursor parsed to NaN because
  // `NaN > 0` is false. Preserve that output for malformed cursor inputs.
  if (Number.isNaN(cursorMs)) return epochMs.length - 1;
  let low = 0;
  let high = epochMs.length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    if (epochMs[middle]! <= cursorMs) low = middle + 1;
    else high = middle;
  }
  return low - 1;
}

function applyPerturbationAt(
  base: number,
  perturbations: readonly Perturbation[],
  at: SeriesTime,
  cursor: SeriesTime,
  forLatest: boolean,
): number {
  const evalAt = forLatest ? cursor : at;
  let value = base;
  let setValue: number | null = null;

  for (const p of perturbations) {
    if (diffMinutes(evalAt, p.from) < 0) continue;
    switch (p.kind) {
      case 'ramp': {
        const hours = Math.max(0, diffMinutes(evalAt, p.from) / 60);
        const raw = p.perHour * hours;
        const delta =
          p.cap !== undefined
            ? Math.sign(p.perHour) * Math.min(Math.abs(p.cap), Math.abs(raw))
            : raw;
        value += delta;
        break;
      }
      case 'step':
        value += p.delta;
        break;
      case 'set':
        setValue = p.value;
        break;
      case 'scale':
        value *= p.factor;
        break;
    }
  }

  return setValue ?? value;
}

/**
 * Exported for B6 tests — evaluate perturbations on a base value at a stamp.
 * `perturbations` must already be filtered to the target series (`p.seriesId === series.id`).
 */
export function applyPerturbations(
  base: number,
  perturbations: readonly Perturbation[],
  at: SeriesTime,
  cursor: SeriesTime,
  forLatest = false,
): number {
  return applyPerturbationAt(base, perturbations, at, cursor, forLatest);
}

export function seriesAt(seriesId: string, state: SessionState): SeriesView {
  const series = SERIES_BY_ID[seriesId];
  if (!series) throw new Error(`Unknown series ${seriesId}`);
  const indexed = seriesStamps(series);
  const { stamps } = indexed;
  const lastVisibleIndex = lastIndexAtOrBefore(indexed.epochMs, timeMilliseconds(state.cursor));
  const allPerturbations = perturbationsFor(state);
  const perturbations = allPerturbations.filter((p) => p.seriesId === seriesId);
  const visible: { at: SeriesTime; value: number | null; rawBase?: number }[] = [];
  let latestBase: { at: SeriesTime; value: number } | undefined;

  for (let i = 0; i <= lastVisibleIndex; i++) {
    const stamp = stamps[i]!;
    const raw = series.points[i];
    if (raw === null) {
      visible.push({ at: stamp, value: null });
      continue;
    }
    latestBase = { at: stamp, value: raw };
    visible.push({
      at: stamp,
      rawBase: raw,
      value: applyPerturbationAt(raw, perturbations, stamp, state.cursor, false),
    });
  }

  let latest = latestBase
    ? {
        at: latestBase.at,
        value: applyPerturbationAt(latestBase.value, perturbations, latestBase.at, state.cursor, true),
      }
    : undefined;
  // A `set` perturbation IS a new observation (an assay landing, e.g. SA01 at
  // 13:00): the live value's stamp is the set's `from`, not the base point's
  // period stamp, so a time-aware reader (the diagnosis discriminator) sees it
  // as newer than the excursion it confirms.
  if (latest) {
    for (const p of perturbations) {
      if (p.kind !== 'set') continue;
      if (diffMinutes(p.from, state.cursor) > 0) continue;
      if (diffMinutes(p.from, latest.at) > 0) latest = { ...latest, at: p.from };
    }
  }

  return { seriesId, cadence: series.cadence, points: visible, latest };
}

export function valueAt(readingId: string, state: SessionState): number | undefined {
  if (HOURLY_FEED_READING_IDS.includes(readingId)) {
    const daily = valueAt('rd-kpi-ore-td', state);
    return daily === undefined ? undefined : daily / HOURLY_FEED_DIVISOR;
  }
  const series = SERIES_BY_READING_ID[readingId];
  if (!series) {
    const seed = READINGS.find((r) => r.id === readingId);
    return seed?.value;
  }
  const view = seriesAt(series.id, state);
  return view.latest?.value;
}

export function readingsAt(state: SessionState): Reading[] {
  return READINGS.map((row) => {
    const projected = valueAt(row.id, state);
    if (projected === undefined) return { ...row };
    return { ...row, value: projected };
  });
}

function visibleNumericPoints(
  seriesId: string,
  state: SessionState,
  from: SeriesTime,
): { at: SeriesTime; value: number }[] {
  const view = seriesAt(seriesId, state);
  return view.points.filter(
    (p): p is { at: SeriesTime; value: number } =>
      p.value !== null && diffMinutes(p.at, from) >= 0 && diffMinutes(p.at, state.cursor) <= 0,
  );
}

export function windowSum(seriesId: string, state: SessionState, from: SeriesTime): number {
  const pts = visibleNumericPoints(seriesId, state, from);
  return pts.reduce((sum, p) => sum + p.value, 0);
}

export function windowMean(seriesId: string, state: SessionState, from: SeriesTime): number {
  const pts = visibleNumericPoints(seriesId, state, from);
  if (pts.length === 0) return 0;
  return pts.reduce((sum, p) => sum + p.value, 0) / pts.length;
}

export type TrendBundle = {
  categories: readonly string[];
  series: readonly {
    name: string;
    points: readonly number[];
    tone: 'warn' | 'info' | 'ok';
  }[];
  references: readonly { value: number; label: string; tone: 'warn' | 'info' | 'ok' | 'neutral' }[];
};

const TREND_SERIES_META: Record<string, { name: string; tone: 'warn' | 'info' | 'ok' }> = {
  'rs-zn-rec': { name: 'Zn', tone: 'warn' },
  'rs-pb-rec': { name: 'Pb', tone: 'info' },
  'rs-ag-rec': { name: 'Ag', tone: 'ok' },
};

export function trendBundle(
  seriesIds: readonly string[],
  state: SessionState,
  opts: { points: number },
): TrendBundle {
  const anchorId = seriesIds[0]!;
  const anchorView = seriesAt(anchorId, state);
  const anchorNumeric = anchorView.points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  const slice = anchorNumeric.slice(-opts.points);
  const categories = slice.map((p) => formatDayLabel(p.at));
  const anchorStamps = slice.map((p) => p.at);

  const series = seriesIds.map((id) => {
    const meta = TREND_SERIES_META[id] ?? { name: id, tone: 'neutral' as const };
    const view = seriesAt(id, state);
    const byStamp = new Map(
      view.points
        .filter((p): p is { at: SeriesTime; value: number } => p.value !== null)
        .map((p) => [p.at, p.value]),
    );
    const pts = anchorStamps.map((at) => byStamp.get(at) ?? 0);
    return { name: meta.name, points: pts, tone: meta.tone };
  });

  const references = seriesIds.map((id) => {
    const meta = TREND_SERIES_META[id] ?? { name: id, tone: 'neutral' as const };
    const seriesDef = SERIES_BY_ID[id];
    const readingId = seriesDef?.readingIds[0];
    const reading = readingId ? READINGS.find((r) => r.id === readingId) : undefined;
    const design = reading?.design ?? 0;
    return { value: design, label: `${meta.name} design ${design}`, tone: meta.tone };
  });

  return { categories, series, references };
}

export { READING_SERIES, SERIES_BY_ID, SERIES_BY_READING_ID };
